import { Component } from '../../core/component.js';
import { store } from '../../store.js';
import { escapeHtml, formatDate, userName, userInitials } from '../../core/utils.js';
import { eventBus } from '../../events.js';
import { api } from '../../api.js';

const SECTION_TITLES = {
    '/': 'Главная',
    '/calendar': 'Календарь',
    '/clients': 'Клиенты',
    '/visits': 'Визиты',
    '/staff': 'Сотрудники',
    '/services': 'Услуги',
    '/finance': 'Финансы',
    '/salary': 'Зарплаты',
    '/warehouse': 'Склад',
    '/loyalty': 'Лояльность',
    '/analytics': 'Аналитика',
    '/online-booking': 'Онлайн-запись',
    '/integrations': 'Интеграции',
    '/support': 'Поддержка',
    '/notifications': 'Уведомления',
    '/settings': 'Настройки',
};

function notifRelativeTime(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'только что';
    if (mins < 60) return `${mins} мин`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} ч`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'вчера';
    if (days < 7) return `${days} дн`;
    return formatDate(dateStr, 'dd.MM');
}

function notifIcon(title) {
    if (!title) return '🔔';
    const t = title.toLowerCase();
    if (t.includes('обращени') || t.includes('поддержк') || t.includes('сообщени')) return '🆘';
    if (t.includes('запис') || t.includes('прием')) return '📅';
    if (t.includes('отмен')) return '❌';
    if (t.includes('оплат') || t.includes('финанс')) return '💳';
    if (t.includes('отзыв')) return '⭐';
    if (t.includes('звонок') || t.includes('пропущ')) return '📞';
    return '🔔';
}

function groupNotifications(items) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups = { today: [], yesterday: [], earlier: [] };

    for (const n of items) {
        const d = new Date(n.created_at);
        if (d >= today) groups.today.push(n);
        else if (d >= yesterday) groups.yesterday.push(n);
        else groups.earlier.push(n);
    }

    return groups;
}

// Subtle notification sound
let _audioCtx = null;
function playNotifSound() {
    try {
        if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const ctx = _audioCtx;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(660, ctx.currentTime + 0.08);
        osc.frequency.setValueAtTime(880, ctx.currentTime + 0.16);
        gain.gain.setValueAtTime(0.06, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.35);
    } catch { /* silent */ }
}

export class Header extends Component {
    constructor(props = {}) {
        super(props);
        this.el.className = 'app-header';
        this.el.id = 'appHeader';
        this.state = {
            profileOpen: false,
            branchOpen: false,
            notifOpen: false,
            notifItems: [],
            unreadCount: 0,
        };
        this._onDocClick = this._onDocClick.bind(this);
        this._onEscKey = this._onEscKey.bind(this);
        this._notifPollTimer = null;
        this._prevUnreadCount = 0;
    }

    _getCurrentTitle() {
        const path = (window.location.hash.slice(1) || '/').split('?')[0];
        for (const [prefix, title] of Object.entries(SECTION_TITLES)) {
            if (prefix === '/' ? path === '/' : path.startsWith(prefix)) {
                return title;
            }
        }
        return '';
    }

    template() {
        const user = store.user;
        const account = store.account;
        const branch = store.branch;
        const branches = account?.branches || [];
        const title = this._getCurrentTitle();
        const { profileOpen, branchOpen } = this.state;

        const initials = userInitials(user);
        const displayName = user ? escapeHtml(userName(user)) : '';

        const branchSelector = branches.length > 1 ? `
            <div class="header-branch">
                <button class="header-branch-btn" id="branchToggle">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 4l6 3 6-3M2 4v7l6 3 6-3V4"/></svg>
                    <span class="header-branch-name">${escapeHtml(branch?.name || 'Филиал')}</span>
                    <svg class="header-branch-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 5l3 3 3-3"/></svg>
                </button>
                ${branchOpen ? `
                    <div class="header-dropdown header-branch-dropdown">
                        <div class="header-branch-dropdown-title">Выберите филиал</div>
                        ${branches.map(b => `
                            <button class="header-dropdown-item header-branch-item ${b.id === branch?.id ? 'header-dropdown-item--active' : ''}" data-branch-id="${b.id}">
                                <span class="header-branch-item-name">${escapeHtml(b.name)}</span>
                                ${b.address ? `<span class="header-branch-item-addr">${escapeHtml(b.address)}</span>` : ''}
                            </button>
                        `).join('')}
                    </div>
                ` : ''}
            </div>
        ` : '';

        const searchShortcut = navigator.platform.includes('Mac') ? '&#8984;K' : 'Ctrl+K';

        return `
            <button class="header-menu-btn" id="menuToggle" aria-label="Меню">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <line x1="3" y1="12" x2="21" y2="12"/>
                    <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
            </button>
            <div class="header-title">${escapeHtml(title)}</div>
            <button class="header-search-btn" id="searchBtn" title="Поиск (${searchShortcut})">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.5">
                    <circle cx="8" cy="8" r="5.5"/><line x1="12" y1="12" x2="16" y2="16"/>
                </svg>
                <span class="header-search-hint">${searchShortcut}</span>
            </button>
            <div class="header-actions">
                <span class="header-online-indicator ${store.isOnline ? 'header-online-indicator--online' : 'header-online-indicator--offline'}" title="${store.isOnline ? 'Онлайн' : 'Офлайн'}"></span>
                ${branchSelector}

                <div class="header-notif-wrapper">
                    <button class="header-notifications" id="notifBtn" title="Уведомления">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5">
                            <path d="M10 2a5 5 0 00-5 5c0 5-2 6-2 6h14s-2-1-2-6a5 5 0 00-5-5z"/>
                            <path d="M8.5 17a1.5 1.5 0 003 0"/>
                        </svg>
                        <span class="header-notif-badge" id="notifBadge" style="display:${this.state.unreadCount > 0 ? '' : 'none'}">${this.state.unreadCount > 99 ? '99+' : this.state.unreadCount}</span>
                    </button>
                    ${this.state.notifOpen ? this._renderNotifDropdown() : ''}
                </div>
                <div class="header-profile">
                    <button class="header-avatar" id="profileToggle" title="${displayName}">
                        ${initials}
                    </button>
                    ${profileOpen ? `
                        <div class="header-dropdown header-profile-dropdown">
                            <div class="header-dropdown-header">${displayName}</div>
                            <a href="#/settings?tab=profile" class="header-dropdown-item">Профиль</a>
                            <a href="#/settings" class="header-dropdown-item">Настройки</a>
                            <hr class="header-dropdown-divider">
                            <button class="header-dropdown-item header-dropdown-item--danger" id="headerLogout">Выйти</button>
                        </div>
                    ` : ''}
                </div>
            </div>
        `;
    }

    _renderNotifDropdown() {
        const items = this.state.notifItems;

        if (items.length === 0) {
            return `
                <div class="header-dropdown notif-dropdown">
                    <div class="notif-dropdown-header">
                        <span>Уведомления</span>
                    </div>
                    <div class="notif-dropdown-list">
                        <div class="notif-dropdown-empty">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-gray-300)" stroke-width="1.5">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                            </svg>
                            <p>Нет уведомлений</p>
                            <p class="notif-dropdown-empty-sub">Здесь появятся уведомления о новых сообщениях, записях и событиях</p>
                        </div>
                    </div>
                </div>
            `;
        }

        const groups = groupNotifications(items);

        const renderItem = (n) => {
            const isRead = n.is_read === true || n.is_read === 't' || n.is_read === 'true';
            const icon = notifIcon(n.title);
            return `
                <div class="notif-dropdown-item ${isRead ? '' : 'notif-dropdown-item--unread'}" data-notif-id="${n.id}" ${n.link ? `data-link="${escapeHtml(n.link)}"` : ''}>
                    <span class="notif-dropdown-icon">${icon}</span>
                    <div class="notif-dropdown-item-content">
                        <div class="notif-dropdown-item-title">${escapeHtml(n.title)}</div>
                        ${n.body ? `<div class="notif-dropdown-item-body">${escapeHtml(n.body)}</div>` : ''}
                    </div>
                    <span class="notif-dropdown-item-time">${notifRelativeTime(n.created_at)}</span>
                </div>
            `;
        };

        const renderGroup = (label, items) => {
            if (items.length === 0) return '';
            return `
                <div class="notif-dropdown-group-label">${label}</div>
                ${items.map(renderItem).join('')}
            `;
        };

        const list = [
            renderGroup('Сегодня', groups.today),
            renderGroup('Вчера', groups.yesterday),
            renderGroup('Ранее', groups.earlier),
        ].filter(Boolean).join('');

        return `
            <div class="header-dropdown notif-dropdown">
                <div class="notif-dropdown-header">
                    <span>Уведомления ${this.state.unreadCount > 0 ? `<span class="notif-header-count">${this.state.unreadCount}</span>` : ''}</span>
                    ${this.state.unreadCount > 0 ? '<button class="notif-mark-all" id="markAllReadBtn">Прочитать все</button>' : ''}
                </div>
                <div class="notif-dropdown-list">${list}</div>
                <div class="notif-dropdown-footer-links">
                    <a href="#/support" class="notif-dropdown-footer-link" id="notifSupportLink">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        Поддержка
                    </a>
                    <a href="#/notifications" class="notif-dropdown-footer-link" id="notifSettingsLink">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
                        Настройки
                    </a>
                </div>
            </div>
        `;
    }

    afterRender() {
        const menuToggle = this.el.querySelector('#menuToggle');
        if (menuToggle) {
            menuToggle.addEventListener('click', () => {
                eventBus.emit('sidebar:toggle');
            });
        }

        const searchBtn = this.el.querySelector('#searchBtn');
        if (searchBtn) {
            searchBtn.addEventListener('click', () => {
                eventBus.emit('command-palette:open');
            });
        }

        const notifBtn = this.el.querySelector('#notifBtn');
        if (notifBtn) {
            notifBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                const opening = !this.state.notifOpen;
                this.setState({
                    notifOpen: opening,
                    profileOpen: false,
                    branchOpen: false,
                });
                if (opening) this._loadNotifications();
            });
        }

        const markAllBtn = this.el.querySelector('#markAllReadBtn');
        if (markAllBtn) {
            markAllBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                try {
                    await api.post('/notifications/mark-all-read');
                    this.setState({ unreadCount: 0, notifItems: this.state.notifItems.map(n => ({ ...n, is_read: true })) });
                    this._prevUnreadCount = 0;
                } catch {}
            });
        }

        this.el.querySelectorAll('[data-notif-id]').forEach(el => {
            el.addEventListener('click', async () => {
                const id = el.dataset.notifId;
                const link = el.dataset.link;
                try { await api.put(`/notifications/${id}/read`); } catch {}
                // Mark read in local state without full re-render
                el.classList.remove('notif-dropdown-item--unread');
                if (link) window.location.hash = link.startsWith('#') ? link : '#' + link;
                this.setState({ notifOpen: false });
                this._pollUnreadCount();
            });
        });

        this.el.querySelectorAll('.notif-dropdown-footer-link').forEach(el => {
            el.addEventListener('click', () => {
                this.setState({ notifOpen: false });
            });
        });

        const profileToggle = this.el.querySelector('#profileToggle');
        if (profileToggle) {
            profileToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                this.setState({
                    profileOpen: !this.state.profileOpen,
                    branchOpen: false,
                    notifOpen: false,
                });
            });
        }

        const branchToggle = this.el.querySelector('#branchToggle');
        if (branchToggle) {
            branchToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                this.setState({
                    branchOpen: !this.state.branchOpen,
                    profileOpen: false,
                    notifOpen: false,
                });
            });
        }

        this.el.querySelectorAll('[data-branch-id]').forEach(btn => {
            btn.addEventListener('click', () => {
                const branches = store.account?.branches || [];
                const branch = branches.find(b => b.id === parseInt(btn.dataset.branchId));
                if (branch) {
                    store.branch = branch;
                    eventBus.emit('branch:changed', branch);
                }
                this.setState({ branchOpen: false });
            });
        });

        const logoutBtn = this.el.querySelector('#headerLogout');
        if (logoutBtn) {
            logoutBtn.addEventListener('click', () => {
                eventBus.emit('auth:logout');
            });
        }

        if (this.state.profileOpen || this.state.branchOpen || this.state.notifOpen) {
            document.addEventListener('click', this._onDocClick, true);
            document.addEventListener('keydown', this._onEscKey, true);
        }
    }

    _onDocClick(e) {
        if (!this.el.contains(e.target)) {
            this._closeDropdowns();
        }
    }

    _onEscKey(e) {
        if (e.key === 'Escape' && (this.state.profileOpen || this.state.branchOpen || this.state.notifOpen)) {
            this._closeDropdowns();
        }
    }

    _closeDropdowns() {
        this.setState({ profileOpen: false, branchOpen: false, notifOpen: false });
        document.removeEventListener('click', this._onDocClick, true);
        document.removeEventListener('keydown', this._onEscKey, true);
    }

    async _loadNotifications() {
        try {
            const result = await api.get('/notifications');
            this.setState({
                notifItems: result?.notifications || [],
                unreadCount: result?.unread_count ?? this.state.unreadCount,
            });
            this._prevUnreadCount = result?.unread_count ?? this.state.unreadCount;
        } catch {}
    }

    async _pollUnreadCount() {
        if (!api.getToken()) return;
        try {
            const result = await api.get('/notifications/unread-count');
            const count = result?.count ?? 0;
            if (count !== this.state.unreadCount) {
                // New notifications arrived
                if (count > this._prevUnreadCount) {
                    this._animateBadge();
                    playNotifSound();
                }
                this._prevUnreadCount = count;
                this.state.unreadCount = count;
                this.setNotificationCount(count);
            }
        } catch {}
    }

    _animateBadge() {
        const badge = this.el.querySelector('#notifBadge');
        const btn = this.el.querySelector('#notifBtn');
        if (badge) {
            badge.classList.remove('notif-badge-pulse');
            void badge.offsetWidth; // force reflow
            badge.classList.add('notif-badge-pulse');
        }
        if (btn) {
            btn.classList.remove('notif-btn-ring');
            void btn.offsetWidth;
            btn.classList.add('notif-btn-ring');
        }
    }

    _startNotifPolling() {
        this._pollUnreadCount();
        this._notifPollTimer = setInterval(() => this._pollUnreadCount(), 15000);
    }

    _stopNotifPolling() {
        if (this._notifPollTimer) {
            clearInterval(this._notifPollTimer);
            this._notifPollTimer = null;
        }
    }

    mount(container) {
        super.mount(container);
        this._unsubHash = () => this.render();
        window.addEventListener('hashchange', this._unsubHash);
        this._unsubUser = store.subscribe('user', () => this.render());
        this._unsubBranch = store.subscribe('branch', () => this.render());
        this._unsubOnline = store.subscribe('isOnline', () => this.render());
        if (api.getToken()) this._startNotifPolling();
        this._unsubLogin = eventBus.on('auth:login', () => this._startNotifPolling());
        this._unsubLogout = eventBus.on('auth:logout', () => this._stopNotifPolling());
        return this;
    }

    destroy() {
        if (this._unsubHash) window.removeEventListener('hashchange', this._unsubHash);
        if (this._unsubUser) this._unsubUser();
        if (this._unsubBranch) this._unsubBranch();
        if (this._unsubOnline) this._unsubOnline();
        this._stopNotifPolling();
        if (this._unsubLogin) this._unsubLogin();
        if (this._unsubLogout) this._unsubLogout();
        document.removeEventListener('click', this._onDocClick, true);
        document.removeEventListener('keydown', this._onEscKey, true);
        super.destroy();
    }

    setNotificationCount(count) {
        const badge = this.el.querySelector('#notifBadge');
        if (badge) {
            badge.textContent = count > 99 ? '99+' : count;
            badge.style.display = count > 0 ? '' : 'none';
        }
    }
}
